import React from 'react';
import type { LucideIcon } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  error?: string;
  helperText?: string;
}

const Input: React.FC<InputProps> = ({ 
  label, 
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  error,
  helperText,
  className = '',
  id,
  ...props 
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#dbeafe',
    marginBottom: '0.25rem',
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: LeftIcon ? '0.75rem 0.75rem 0.75rem 2.75rem' : '0.75rem',
    paddingRight: RightIcon ? '2.75rem' : '0.75rem',
    background: 'rgba(30, 41, 59, 0.8)',
    border: error ? '1px solid #f87171' : '1px solid rgba(96, 165, 250, 0.3)',
    borderRadius: '0.5rem',
    color: '#dbeafe',
    fontSize: '1rem',
    outline: 'none',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(8px)',
  };

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  const leftIconStyle: React.CSSProperties = {
    ...iconStyle,
    left: '0.75rem',
  };

  const rightIconStyle: React.CSSProperties = {
    ...iconStyle,
    right: '0.75rem',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: '#f87171',
    marginTop: '0.25rem',
  };

  const helperStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: '#94a3b8',
    marginTop: '0.25rem',
  };

  return (
    <div style={containerStyle} className={className}>
      {label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
        </label>
      )}
      
      <div style={inputWrapperStyle}>
        {LeftIcon && <LeftIcon size={20} style={leftIconStyle} />}
        
        <input
          id={id}
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = error ? '#f87171' : '#60a5fa';
            e.target.style.boxShadow = error 
              ? '0 0 0 3px rgba(248, 113, 113, 0.1)' 
              : '0 0 0 3px rgba(96, 165, 250, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? '#f87171' : 'rgba(96, 165, 250, 0.3)';
            e.target.style.boxShadow = 'none';
          }}
          {...props}
        />
        
        {RightIcon && <RightIcon size={20} style={rightIconStyle} />}
      </div>
      
      {error && <span style={errorStyle}>{error}</span>}
      {helperText && !error && <span style={helperStyle}>{helperText}</span>}
    </div>
  );
};

export default Input; 